import { useState, useCallback } from 'react'

import { useTranslateTextMutation } from '../api/core'
import { UserLanguage } from '../features/account/types/User'
import { Review, ReviewContentFields } from '../features/game/types/GameReview'
import { mapUserLanguageToBackend } from '../utils/languageMapping'

/**
 * Hook for translating simple HTML content
 * Returns translation state and toggle function
 */
export const useContentTranslation = () => {
  const [translateText, { isLoading: isTranslating }] = useTranslateTextMutation()
  const [translatedContent, setTranslatedContent] = useState<string | null>(null)
  const [translationError, setTranslationError] = useState<string | null>(null)

  const handleTranslateClick = async (content: string, userLanguage: UserLanguage) => {
    if (translatedContent) {
      setTranslatedContent(null)
      setTranslationError(null)
    } else if (content) {
      try {
        setTranslationError(null)
        const data = await translateText({
          text: content,
          targetLanguage: mapUserLanguageToBackend(userLanguage),
        }).unwrap()

        if (data.status === 'SUCCESS') {
          setTranslatedContent(data.text)
        }
      } catch (error: any) {
        setTranslationError(error?.data?.message || error?.message || 'Translation failed')
      }
    }
  }

  const resetTranslation = useCallback(() => {
    setTranslatedContent(null)
    setTranslationError(null)
  }, [])

  const displayContent = (originalContent: string) => translatedContent || originalContent

  return {
    isTranslating,
    translatedContent,
    translationError,
    handleTranslateClick,
    resetTranslation,
    displayContent,
  }
}

export type TranslationState = {
  isTranslating: boolean
  error: string | null
  hasTranslation: boolean
  translatedReview?: Review
}

/**
 * Hook for translating complex Review objects with multiple content fields
 * Translates all fields (genreAndStyle, featuresAndProgression, monetization, specialNotes)
 */
export const useStreamingTranslation = () => {
  const [translateText] = useTranslateTextMutation()
  const [translationState, setTranslationState] = useState<TranslationState>({
    isTranslating: false,
    error: null,
    hasTranslation: false,
  })

  const startTranslation = useCallback(
    async (review: Review, targetLanguage: UserLanguage) => {
      if (!review?.content) return

      setTranslationState((prev) => ({
        ...prev,
        isTranslating: true,
        error: null,
      }))

      try {
        const mutableReview = JSON.parse(JSON.stringify(review))

        const targetLang = mapUserLanguageToBackend(targetLanguage)

        const contentFields = Object.values(ReviewContentFields)

        for (const field of contentFields) {
          const englishContent = mutableReview.content[field]?.en
          if (!englishContent) continue

          const data = await translateText({
            text: englishContent,
            targetLanguage: targetLang,
          }).unwrap()

          if (data.status === 'SUCCESS') {
            mutableReview.content[field][targetLanguage] = data.text
          }
        }

        setTranslationState((prev) => ({
          ...prev,
          isTranslating: false,
          hasTranslation: true,
          translatedReview: mutableReview,
        }))
      } catch (error: any) {
        setTranslationState((prev) => ({
          ...prev,
          isTranslating: false,
          error: error?.data?.message || error?.message || 'Translation failed',
        }))
      }
    },
    [translateText]
  )

  const resetTranslation = useCallback(() => {
    setTranslationState({
      isTranslating: false,
      error: null,
      hasTranslation: false,
      translatedReview: undefined,
    })
  }, [])

  const toggleTranslation = useCallback(
    (review: Review, targetLanguage: UserLanguage) => {
      if (translationState.hasTranslation) {
        resetTranslation()
      } else {
        startTranslation(review, targetLanguage)
      }
    },
    [startTranslation, resetTranslation, translationState.hasTranslation]
  )

  return {
    ...translationState,
    toggleTranslation,
    resetTranslation,
  }
}
